import type { ChangeEvent, FormEvent } from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Person24Regular,
  Mail24Regular,
  Call24Regular,
  Building24Regular,
  Camera24Regular,
  LockClosed24Regular,
  Eye20Regular,
  EyeOff20Regular,
  ArrowSync20Regular,
  Checkmark16Filled,
  Warning20Regular,
  Save20Regular,
  Dismiss20Regular,
} from "@fluentui/react-icons";
import { PageHeader } from "../../components/PageHeader";
import { AvatarCropModal } from "../../components/AvatarCropModal";
import {
  getUserProfile,
  removeAvatar,
  updateUserProfile,
  uploadAvatar,
  type UserProfile,
} from "../../profile/profileApi";
import {
  calculatePasswordStrength,
  generatePassword,
} from "../../users/passwordUtils";
import { useAuth } from "../../auth/AuthContext";

const defaultProfileState = {
  nome: "",
  apelido: "",
  email: "",
  telefone: "",
  departamento: "",
};

type ProfileLocationState = {
  from?: {
    pathname?: string;
    search?: string;
  };
};

const parseSubmitError = (error: unknown): string => {
  if (error instanceof Error && error.message) {
    try {
      const parsed = JSON.parse(error.message) as {
        errors?: Record<string, string[]>;
        detail?: string;
        title?: string;
      };

      if (parsed.errors) {
        const messages = Object.values(parsed.errors).flat();
        if (messages.length > 0) {
          return messages.join(" ");
        }
      }

      if (parsed.detail) {
        return parsed.detail;
      }

      if (parsed.title) {
        return parsed.title;
      }
    } catch {
      return error.message;
    }

    return error.message;
  }

  return "Não foi possível atualizar o perfil.";
};

export function PerfilView() {
  const navigate = useNavigate();
  const location = useLocation();
  const [nome, setNome] = useState(defaultProfileState.nome);
  const [apelido, setApelido] = useState(defaultProfileState.apelido);
  const [email, setEmail] = useState(defaultProfileState.email);
  const [telefone, setTelefone] = useState(defaultProfileState.telefone);
  const [departamento, setDepartamento] = useState(
    defaultProfileState.departamento,
  );
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [savedProfile, setSavedProfile] = useState<UserProfile | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploadingAvatar, setIsUploadingAvatar] =
    useState(false);
  const [loadError, setLoadError] = useState<string | null>(
    null,
  );
  const [submitError, setSubmitError] = useState<string | null>(
    null,
  );
  const [resetPassword, setResetPassword] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] =
    useState(false);
  const [tempImageUrl, setTempImageUrl] = useState<string | null>(null);
  const [tempImageFile, setTempImageFile] = useState<File | null>(
    null,
  );
  const [showCropModal, setShowCropModal] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Trabalhador só de Folha de Horas: vê o próprio perfil mas não o edita (nem dados, nem foto). O
  // Administrador e quem tem qualquer outra funcionalidade continuam a poder editar-se.
  const { user } = useAuth();
  // Sem qualquer funcionalidade de backoffice (só Folha de Horas, ou nada): perfil só-leitura. O
  // Administrador é excluído pela primeira condição, por isso um não-admin sem features também cai
  // aqui — a par da autoridade no servidor (SelfServiceProfilePolicy).
  const isHoursOnlyWorker =
    !(user?.roles?.includes("Administrator") ?? false) &&
    (user?.features ?? []).every((feature) => feature === "folha-de-horas");

  const isReadOnly = isLoading || isSaving || isHoursOnlyWorker;

  const resolveReturnTarget = () => {
    const state = location.state as ProfileLocationState | null;
    const from = state?.from;
    if (from?.pathname && from.pathname !== "/perfil") {
      return `${from.pathname}${from.search ?? ""}`;
    }
    return "/dashboard";
  };

  useEffect(() => {
    let isActive = true;

    const loadProfile = async () => {
      setIsLoading(true);
      setLoadError(null);
      try {
        const profile = await getUserProfile();
        if (!isActive) return;
        setSavedProfile(profile);
        setNome(profile.firstName ?? "");
        setApelido(profile.lastName ?? "");
        setEmail(profile.email ?? "");
        setTelefone(profile.phoneNumber ?? "");
        setDepartamento(profile.department ?? "");
        setAvatarUrl(profile.avatarUrl ?? null);
      } catch (error) {
        if (!isActive) return;
        console.error("Erro ao carregar o perfil:", error);
        setLoadError("Não foi possível carregar o perfil.");
      } finally {
        if (isActive) {
          setIsLoading(false);
        }
      }
    };

    loadProfile();

    return () => {
      isActive = false;
    };
  }, []);

  useEffect(() => {
    return () => {
      if (tempImageUrl) {
        URL.revokeObjectURL(tempImageUrl);
      }
    };
  }, [tempImageUrl]);

  const passwordStrength = useMemo(
    () => (password ? calculatePasswordStrength(password) : null),
    [password],
  );

  const passwordRequirements = useMemo(
    () => ({
      minLength: password.length >= 6,
      hasSpecial: /[^A-Za-z0-9]/.test(password),
    }),
    [password],
  );

  const passwordMeetsPolicy = Object.values(
    passwordRequirements,
  ).every(Boolean);

  const passwordsMatch =
    password.length > 0 &&
    confirmPassword.length > 0 &&
    password === confirmPassword;

  const handleAvatarClick = () => {
    if (isLoading || isUploadingAvatar) return;
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (isUploadingAvatar) {
      event.target.value = "";
      return;
    }

    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      alert("Por favor selecione um ficheiro de imagem válido.");
      event.target.value = "";
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      alert("O ficheiro deve ter no máximo 2MB.");
      event.target.value = "";
      return;
    }

    const previewUrl = URL.createObjectURL(file);
    setTempImageFile(file);
    setTempImageUrl(previewUrl);
    setShowCropModal(true);

    event.target.value = "";
  };

  const resetTempImage = () => {
    if (tempImageUrl) {
      URL.revokeObjectURL(tempImageUrl);
    }
    setTempImageUrl(null);
    setTempImageFile(null);
    setShowCropModal(false);
  };

  const handleCropComplete = async (_imageUrl: string) => {
    if (!tempImageFile) {
      resetTempImage();
      return;
    }

    setIsUploadingAvatar(true);
    try {
      const response = await uploadAvatar(tempImageFile);
      setAvatarUrl(response.avatarUrl);
      setSavedProfile((previous) =>
        previous
          ? { ...previous, avatarUrl: response.avatarUrl }
          : previous,
      );
    } catch (error) {
      console.error("Erro ao atualizar o avatar:", error);
    } finally {
      setIsUploadingAvatar(false);
      resetTempImage();
    }
  };

  const handleCropCancel = () => {
    resetTempImage();
  };

  const handleRemoveAvatar = async () => {
    if (isLoading) return;
    try {
      const response = await removeAvatar();
      setAvatarUrl(response.avatarUrl ? response.avatarUrl : null);
      setSavedProfile((previous) =>
        previous
          ? { ...previous, avatarUrl: response.avatarUrl || null }
          : previous,
      );
    } catch (error) {
      console.error("Erro ao remover o avatar:", error);
    }
  };

  const handleGeneratePassword = () => {
    const generated = generatePassword();
    setPassword(generated);
    setConfirmPassword(generated);
    setShowPassword(true);
    setShowConfirmPassword(true);
    setSubmitError(null);
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitError(null);

    if (resetPassword) {
      if (!passwordMeetsPolicy) {
        setSubmitError(
          "A password deve ter pelo menos 6 caracteres e incluir um caracter especial.",
        );
        return;
      }

      if (!passwordsMatch) {
        setSubmitError("As passwords não coincidem.");
        return;
      }
    }

    setIsSaving(true);
    try {
      const profile = await updateUserProfile({
        firstName: nome.trim(),
        lastName: apelido.trim(),
        phoneNumber: telefone.trim() ? telefone.trim() : null,
        department: departamento.trim() ? departamento.trim() : null,
        newPassword: resetPassword ? password : undefined,
      });
      setSavedProfile(profile);
      setNome(profile.firstName ?? "");
      setApelido(profile.lastName ?? "");
      setEmail(profile.email ?? "");
      setTelefone(profile.phoneNumber ?? "");
      setDepartamento(profile.department ?? "");
      setAvatarUrl(profile.avatarUrl ?? null);
      setResetPassword(false);
      setPassword("");
      setConfirmPassword("");
      setShowPassword(false);
      setShowConfirmPassword(false);
      const returnTarget = resolveReturnTarget();
      navigate(returnTarget, {
        replace: true,
        state: { toast: "Guardado com sucesso" },
      });
    } catch (error) {
      console.error("Erro ao guardar o perfil:", error);
      setSubmitError(parseSubmitError(error));
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (!savedProfile) return;
    setNome(savedProfile.firstName ?? "");
    setApelido(savedProfile.lastName ?? "");
    setEmail(savedProfile.email ?? "");
    setTelefone(savedProfile.phoneNumber ?? "");
    setDepartamento(savedProfile.department ?? "");
    setAvatarUrl(savedProfile.avatarUrl ?? null);
    setSubmitError(null);
    setResetPassword(false);
    setPassword("");
    setConfirmPassword("");
    setShowPassword(false);
    setShowConfirmPassword(false);
  };

  const actions = isHoursOnlyWorker ? (
    <span className="text-sm text-secondary-text">
      Para alterar os seus dados, contacte um administrador.
    </span>
  ) : (
    <div className="flex items-center gap-3">
      <button
        type="button"
        className="px-4 py-2 bg-secondary hover:bg-accent border border-border transition-colors flex items-center gap-2 min-h-[44px] disabled:opacity-50 disabled:cursor-not-allowed"
        style={{ borderRadius: "var(--radius-button)" }}
        onClick={handleCancel}
        disabled={isSaving || isLoading}
      >
        <Dismiss20Regular className="w-5 h-5" />
        <span className="hidden sm:inline">Cancelar</span>
      </button>
      <button
        className="px-4 py-2 bg-primary text-primary-foreground hover:opacity-90 transition-colors flex items-center gap-2 min-h-[44px] disabled:opacity-50 disabled:cursor-not-allowed"
        style={{ borderRadius: "var(--radius-button)" }}
        onClick={() => {
          const form = document.getElementById(
            "perfil-form",
          ) as HTMLFormElement | null;
          form?.requestSubmit();
        }}
        disabled={isSaving || isLoading}
      >
        <Save20Regular className="w-5 h-5" />
        {isSaving ? "A guardar..." : "Guardar alterações"}
      </button>
    </div>
  );

  const getInitials = () => {
    const first = nome.trim().charAt(0);
    const last = apelido.trim().charAt(0);
    return `${first}${last}`.toUpperCase();
  };

  const accessProfileLabel = savedProfile?.roles?.includes(
    "Administrator",
  )
    ? "Administrador"
    : "Utilizador";

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title="O Meu Perfil"
        subtitle="Gerir as suas informações pessoais e definições de conta"
        actions={actions}
      />

      <div className="flex-1 overflow-y-auto bg-background">
        <div className="p-4 md:p-6">
          <form id="perfil-form" onSubmit={handleSubmit}>
            {loadError && (
              <p className="mb-4 text-destructive">{loadError}</p>
            )}
            {submitError && (
              <p className="mb-4 text-destructive">{submitError}</p>
            )}
            <div
              className="bg-card p-6 mb-6"
              style={{ borderRadius: "var(--radius-card)" }}
            >
              <h2 className="text-card-foreground mb-6 flex items-center gap-2">
                <Camera24Regular />
                Fotografia de Perfil
              </h2>

              <div className="flex flex-col md:flex-row items-center gap-6">
                <div className="relative">
                  <div
                    className="w-32 h-32 bg-primary/10 flex items-center justify-center text-primary overflow-hidden"
                    style={{ borderRadius: "var(--radius-full)" }}
                  >
                    {avatarUrl ? (
                      <img
                        src={avatarUrl}
                        alt="Avatar"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span style={{ fontSize: "3rem" }}>
                        {getInitials()}
                      </span>
                    )}
                  </div>
                  {!isHoursOnlyWorker && (
                    <button
                      type="button"
                      onClick={handleAvatarClick}
                      className="absolute bottom-0 right-0 w-10 h-10 bg-primary text-primary-foreground hover:opacity-90 transition-all flex items-center justify-center"
                      style={{ borderRadius: "var(--radius-full)" }}
                      disabled={isUploadingAvatar || isLoading}
                    >
                      <Camera24Regular className="w-5 h-5" />
                    </button>
                  )}
                </div>

                <div className="flex-1 text-center md:text-left">
                  {isHoursOnlyWorker ? (
                    <p className="text-card-foreground mb-2">
                      A sua fotografia é gerida por um administrador.
                    </p>
                  ) : (
                    <>
                      <p className="text-card-foreground mb-2">
                        Clique no ícone da câmara para alterar a sua fotografia
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Formatos aceites: JPG, PNG. Tamanho máximo: 2MB
                      </p>
                      {avatarUrl && (
                        <button
                          type="button"
                          onClick={handleRemoveAvatar}
                          className="mt-3 text-sm text-destructive hover:underline"
                        >
                          Remover fotografia
                        </button>
                      )}
                    </>
                  )}
                </div>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handleFileChange}
                  className="hidden"
                />
              </div>
            </div>

            <div
              className="bg-card p-6 mb-6"
              style={{ borderRadius: "var(--radius-card)" }}
            >
              <h2 className="text-card-foreground mb-6 flex items-center gap-2">
                <Person24Regular />
                Informação Pessoal
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label
                    htmlFor="nome"
                    className="block text-card-foreground mb-2"
                  >
                    Nome <span className="text-destructive">*</span>
                  </label>
                  <input
                    id="nome"
                    type="text"
                    value={nome}
                    onChange={(event) => setNome(event.target.value)}
                    required
                    placeholder="Ex: João"
                    className="w-full h-10 px-3 bg-[var(--input-background)] border border-[var(--border)] text-card-foreground placeholder:text-[var(--placeholder-text)] outline-none transition-all focus:border-[var(--ring)] focus:ring-2 focus:ring-[var(--ring)] focus:ring-opacity-20"
                    style={{ borderRadius: "var(--radius)" }}
                    disabled={isReadOnly}
                  />
                </div>

                <div>
                  <label
                    htmlFor="apelido"
                    className="block text-card-foreground mb-2"
                  >
                    Apelido <span className="text-destructive">*</span>
                  </label>
                  <input
                    id="apelido"
                    type="text"
                    value={apelido}
                    onChange={(event) =>
                      setApelido(event.target.value)
                    }
                    required
                    placeholder="Ex: Santos"
                    className="w-full h-10 px-3 bg-[var(--input-background)] border border-[var(--border)] text-card-foreground placeholder:text-[var(--placeholder-text)] outline-none transition-all focus:border-[var(--ring)] focus:ring-2 focus:ring-[var(--ring)] focus:ring-opacity-20"
                    style={{ borderRadius: "var(--radius)" }}
                    disabled={isReadOnly}
                  />
                </div>

                <div>
                  <label
                    htmlFor="email"
                    className="block text-card-foreground mb-2"
                  >
                    Email
                  </label>
                  <div className="relative">
                    <Mail24Regular className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <input
                      id="email"
                      type="email"
                      value={email}
                      readOnly
                      className="w-full h-10 pl-10 pr-3 bg-muted border border-border text-muted-foreground cursor-not-allowed"
                      style={{ borderRadius: "var(--radius)" }}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    O email não pode ser alterado. Contacte o administrador.
                  </p>
                </div>

                <div>
                  <label
                    htmlFor="telefone"
                    className="block text-card-foreground mb-2"
                  >
                    Telefone
                  </label>
                  <div className="relative">
                    <Call24Regular className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <input
                      id="telefone"
                      type="tel"
                      value={telefone}
                      onChange={(event) =>
                        setTelefone(event.target.value)
                      }
                      placeholder="+351 210 000 000"
                      className="w-full h-10 pl-10 pr-3 bg-[var(--input-background)] border border-[var(--border)] text-card-foreground placeholder:text-[var(--placeholder-text)] outline-none transition-all focus:border-[var(--ring)] focus:ring-2 focus:ring-[var(--ring)] focus:ring-opacity-20"
                      style={{ borderRadius: "var(--radius)" }}
                      disabled={isReadOnly}
                    />
                  </div>
                </div>

                <div className="md:col-span-2">
                  <label
                    htmlFor="departamento"
                    className="block text-card-foreground mb-2"
                  >
                    Departamento
                  </label>
                  <div className="relative">
                    <Building24Regular className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <select
                      id="departamento"
                      value={departamento}
                      onChange={(event) =>
                        setDepartamento(event.target.value)
                      }
                      className="w-full h-10 pl-10 pr-3 bg-[var(--input-background)] border border-[var(--border)] text-card-foreground outline-none transition-all focus:border-[var(--ring)] focus:ring-2 focus:ring-[var(--ring)] focus:ring-opacity-20"
                      style={{ borderRadius: "var(--radius)" }}
                      disabled={isReadOnly}
                    >
                      <option value="Direção">Direção</option>
                      <option value="Comercial">Comercial</option>
                      <option value="Obras">Obras</option>
                      <option value="Financeiro">Financeiro</option>
                      <option value="Recursos Humanos">
                        Recursos Humanos
                      </option>
                      <option value="Compras">Compras</option>
                      <option value="Qualidade">Qualidade</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            <div
              className="bg-card p-6 mb-6"
              style={{ borderRadius: "var(--radius-card)" }}
            >
              <h2 className="text-card-foreground mb-4 flex items-center gap-2">
                <LockClosed24Regular />
                Redefinir Password
              </h2>

              {!resetPassword ? (
                <div>
                  <p className="text-sm text-[var(--muted-foreground)] mb-4">
                    Crie uma nova password para a sua conta. A password antiga será substituída.
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      setResetPassword(true);
                      setPassword("");
                      setConfirmPassword("");
                      setShowPassword(false);
                      setShowConfirmPassword(false);
                      setSubmitError(null);
                    }}
                    className="h-10 px-4 bg-[var(--accent)] border border-[var(--border)] text-card-foreground hover:bg-[var(--accent)]/80 transition-all flex items-center gap-2"
                    style={{ borderRadius: "var(--radius-button)" }}
                    disabled={isReadOnly}
                  >
                    <LockClosed24Regular className="w-5 h-5" />
                    Redefinir password
                  </button>
                </div>
              ) : (
                <div className="space-y-6">
                  <div>
                    <button
                      type="button"
                      onClick={handleGeneratePassword}
                      className="h-10 px-4 bg-[var(--accent)] border border-[var(--border)] text-card-foreground hover:bg-[var(--accent)]/80 transition-all flex items-center gap-2"
                      style={{ borderRadius: "var(--radius-button)" }}
                      disabled={isReadOnly}
                    >
                      <ArrowSync20Regular className="w-5 h-5" />
                      Gerar password automaticamente
                    </button>
                    <p className="text-xs text-[var(--muted-foreground)] mt-2">
                      Gera uma password segura de 16 caracteres
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label
                        htmlFor="password"
                        className="block text-card-foreground mb-2"
                      >
                        Nova password{" "}
                        <span className="text-[var(--destructive)]">
                          *
                        </span>
                      </label>
                      <div className="relative">
                        <LockClosed24Regular className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted-foreground)]" />
                        <input
                          id="password"
                          type={showPassword ? "text" : "password"}
                          value={password}
                          onChange={(event) =>
                            setPassword(event.target.value)
                          }
                          required={resetPassword}
                          autoComplete="new-password"
                          placeholder="Digite a nova password"
                          className="w-full h-10 pl-10 pr-10 bg-[var(--input-background)] border border-[var(--border)] text-card-foreground placeholder:text-[var(--placeholder-text)] outline-none transition-all focus:border-[var(--ring)] focus:ring-2 focus:ring-[var(--ring)] focus:ring-opacity-20"
                          style={{ borderRadius: "var(--radius)" }}
                          disabled={isReadOnly}
                        />
                        <button
                          type="button"
                          onClick={() =>
                            setShowPassword((previous) => !previous)
                          }
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--muted-foreground)] hover:text-card-foreground transition-colors"
                          disabled={isReadOnly}
                        >
                          {showPassword ? (
                            <EyeOff20Regular />
                          ) : (
                            <Eye20Regular />
                          )}
                        </button>
                      </div>
                      {passwordStrength && (
                        <div className="mt-2 flex items-center gap-2">
                          <div className="flex-1 h-1.5 bg-[var(--accent)] rounded-full overflow-hidden">
                            <div
                              className="h-full transition-all duration-300"
                              style={{
                                width: `${(passwordStrength.score / 5) * 100}%`,
                                backgroundColor: passwordStrength.color,
                              }}
                            />
                          </div>
                          <span
                            className="text-xs"
                            style={{
                              color: passwordStrength.color,
                            }}
                          >
                            {passwordStrength.label}
                          </span>
                        </div>
                      )}
                      {password.length > 0 && (
                        <div className="mt-2 space-y-1 text-xs">
                          <div
                            className={`flex items-center gap-1.5 ${
                              passwordRequirements.minLength
                                ? "text-[var(--success)]"
                                : "text-[var(--muted-foreground)]"
                            }`}
                          >
                            {passwordRequirements.minLength ? (
                              <Checkmark16Filled />
                            ) : (
                              <Dismiss20Regular />
                            )}
                            <span>6 ou mais caracteres</span>
                          </div>
                          <div
                            className={`flex items-center gap-1.5 ${
                              passwordRequirements.hasSpecial
                                ? "text-[var(--success)]"
                                : "text-[var(--muted-foreground)]"
                            }`}
                          >
                            {passwordRequirements.hasSpecial ? (
                              <Checkmark16Filled />
                            ) : (
                              <Dismiss20Regular />
                            )}
                            <span>1 caracter especial</span>
                          </div>
                        </div>
                      )}
                      {!passwordMeetsPolicy && (
                        <div className="mt-3 flex items-start gap-2 text-xs text-[var(--destructive)]">
                          <Warning20Regular className="mt-0.5" />
                          <span>
                            A password só será gravada se cumprir os requisitos mínimos acima.
                            Caso contrário, a alteração não será aplicada.
                          </span>
                        </div>
                      )}
                    </div>

                    <div>
                      <label
                        htmlFor="confirmPassword"
                        className="block text-card-foreground mb-2"
                      >
                        Confirmar password{" "}
                        <span className="text-[var(--destructive)]">
                          *
                        </span>
                      </label>
                      <div className="relative">
                        <LockClosed24Regular className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted-foreground)]" />
                        <input
                          id="confirmPassword"
                          type={
                            showConfirmPassword ? "text" : "password"
                          }
                          value={confirmPassword}
                          onChange={(event) =>
                            setConfirmPassword(event.target.value)
                          }
                          required={resetPassword}
                          autoComplete="new-password"
                          placeholder="Confirme a nova password"
                          className="w-full h-10 pl-10 pr-10 bg-[var(--input-background)] border border-[var(--border)] text-card-foreground placeholder:text-[var(--placeholder-text)] outline-none transition-all focus:border-[var(--ring)] focus:ring-2 focus:ring-[var(--ring)] focus:ring-opacity-20"
                          style={{ borderRadius: "var(--radius)" }}
                          disabled={isReadOnly}
                        />
                        <button
                          type="button"
                          onClick={() =>
                            setShowConfirmPassword((previous) => !previous)
                          }
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--muted-foreground)] hover:text-card-foreground transition-colors"
                          disabled={isReadOnly}
                        >
                          {showConfirmPassword ? (
                            <EyeOff20Regular />
                          ) : (
                            <Eye20Regular />
                          )}
                        </button>
                      </div>
                      {confirmPassword && (
                        <div className="mt-2">
                          {passwordsMatch ? (
                            <div className="flex items-center gap-1.5 text-xs text-[var(--success)]">
                              <Checkmark16Filled />
                              <span>As passwords coincidem</span>
                            </div>
                          ) : (
                            <div className="text-xs text-[var(--destructive)]">
                              As passwords não coincidem
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    <button
                      type="button"
                      onClick={() => {
                        setResetPassword(false);
                        setPassword("");
                        setConfirmPassword("");
                        setShowPassword(false);
                        setShowConfirmPassword(false);
                      }}
                      className="text-sm text-[var(--muted-foreground)] hover:text-card-foreground transition-colors"
                      disabled={isReadOnly}
                    >
                      Cancelar redefinição de password
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div
              className="bg-card p-6 mb-6"
              style={{ borderRadius: "var(--radius-card)" }}
            >
              <h2 className="text-card-foreground mb-6">
                Informação da Conta
              </h2>

              <div className="space-y-4">
                <div className="flex justify-between items-center py-3 border-b border-border">
                  <span className="text-muted-foreground">
                    Perfil de Acesso
                  </span>
                  <span className="text-card-foreground">
                    {accessProfileLabel}
                  </span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-border">
                  <span className="text-muted-foreground">Estado</span>
                  <span
                    className="inline-flex items-center gap-2 px-3 py-1 bg-green-500/10 text-green-700"
                    style={{ borderRadius: "var(--radius)" }}
                  >
                    Ativo
                  </span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-border">
                  <span className="text-muted-foreground">
                    Membro desde
                  </span>
                  <span className="text-card-foreground">
                    20/03/2023
                  </span>
                </div>
                <div className="flex justify-between items-center py-3">
                  <span className="text-muted-foreground">
                    Último acesso
                  </span>
                  <span className="text-card-foreground">
                    Hoje às 11:15
                  </span>
                </div>
              </div>
            </div>

            <div className="flex gap-3 md:hidden">
              <button
                type="button"
                onClick={handleCancel}
                disabled={isSaving || isLoading}
                className="flex-1 h-10 bg-secondary border border-border text-[var(--secondary-foreground)] hover:bg-accent transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                style={{ borderRadius: "var(--radius-button)" }}
              >
                <Dismiss20Regular />
                Cancelar
              </button>
              <button
                type="submit"
                disabled={isSaving || isLoading}
                className="flex-1 h-10 bg-primary text-primary-foreground hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                style={{ borderRadius: "var(--radius-button)" }}
              >
                <Save20Regular />
                {isSaving ? "A guardar..." : "Guardar"}
              </button>
            </div>
          </form>
        </div>
      </div>

      <AvatarCropModal
        imageUrl={tempImageUrl}
        onComplete={handleCropComplete}
        onCancel={handleCropCancel}
        show={showCropModal}
      />
    </div>
  );
}
